import React from "react";
import { createPortal } from "react-dom";
import { Search, ChevronDown, Check, X } from "lucide-react";
import { colors, fonts, shadows } from "../../theme";
import { hostOf, techniqueOf, systemDisplayLabel } from "./drafting";

// Searchable, host-grouped system combobox. Replaces the plain <select> so users can:
//  • type-to-filter by host OR technique,
//  • scan options grouped under subtle environment (host) headers, alphabetical,
//  • have the LAST-USED environment pinned to the top (remember-last-host).
// Display-only — labels/sorting derive from existing KB data; no math touched.
export default function SystemSelect({ value, onChange, options, placeholder = "Select a system…", testId = "system-select", lastHost = null, onPickHost }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [rect, setRect] = React.useState(null);
  const [active, setActive] = React.useState(0);
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);
  const listRef = React.useRef(null);

  const selected = (options || []).find((s) => s.id === value) || null;

  const place = React.useCallback(() => {
    const el = btnRef.current;
    if (el) setRect(el.getBoundingClientRect());
  }, []);

  React.useEffect(() => {
    if (!open) return;
    place();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => place();
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, place]);

  const toggle = () => { setQ(""); setOpen((o) => !o); };

  const pick = (s) => {
    onChange?.(s.id);
    onPickHost?.(hostOf(s));
    setOpen(false);
  };

  // Group filtered options by host; pin the last-used host group to the top.
  const groups = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = (options || []).filter((s) => {
      if (!term) return true;
      return systemDisplayLabel(s).toLowerCase().includes(term)
        || techniqueOf(s).toLowerCase().includes(term)
        || hostOf(s).toLowerCase().includes(term);
    });
    const byHost = {};
    list.forEach((s) => {
      const h = hostOf(s) || "Other";
      (byHost[h] = byHost[h] || []).push(s);
    });
    const hosts = Object.keys(byHost).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    Object.values(byHost).forEach((arr) => arr.sort((a, b) => techniqueOf(a).localeCompare(techniqueOf(b), undefined, { sensitivity: "base" })));
    // Pin last-used host to the top (only when not actively searching).
    if (!term && lastHost && byHost[lastHost]) {
      const rest = hosts.filter((h) => h !== lastHost);
      return [lastHost, ...rest].map((h) => ({ host: h, items: byHost[h], recent: h === lastHost }));
    }
    return hosts.map((h) => ({ host: h, items: byHost[h], recent: false }));
  }, [options, q, lastHost]);

  const total = groups.reduce((n, g) => n + g.items.length, 0);
  // Flat option list in render order — the target of arrow-key navigation.
  const flat = React.useMemo(() => groups.flatMap((g) => g.items), [groups]);

  // Reset / clamp the keyboard-active index whenever the visible list changes.
  React.useEffect(() => {
    if (!open) return;
    const sel = flat.findIndex((s) => s.id === value);
    setActive(sel >= 0 ? sel : 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { setActive((i) => Math.min(Math.max(0, i), Math.max(0, flat.length - 1))); }, [flat.length]);
  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-opt-index="${active}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const flatIndex = React.useMemo(() => {
    const m = {}; flat.forEach((s, i) => { m[s.id] = i; }); return m;
  }, [flat]);

  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Home") { e.preventDefault(); setActive(0); }
    else if (e.key === "End") { e.preventDefault(); setActive(flat.length - 1); }
    else if (e.key === "Enter") { e.preventDefault(); if (flat[active]) pick(flat[active]); }
  };

  return (
    <>
      <button ref={btnRef} type="button" data-testid={testId} onClick={toggle}
        style={{ ...S.trigger, ...(open ? S.triggerOpen : {}) }}>
        <span style={{ ...S.triggerText, color: selected ? colors.textPrimary : colors.textTertiary }}>
          {selected ? systemDisplayLabel(selected) : placeholder}
        </span>
        <ChevronDown size={15} style={{ flexShrink: 0, color: colors.textTertiary, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>
      {open && rect && createPortal(
        <div ref={popRef} data-testid={`${testId}-pop`}
          style={{ ...S.pop, left: rect.left, top: rect.bottom + 6, width: Math.max(rect.width, 280) }}>
          <div style={S.searchRow}>
            <Search size={14} style={{ color: colors.textTertiary, flexShrink: 0 }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey} placeholder="Search host or technique…"
              data-testid={`${testId}-search`} style={S.searchInput} />
            {q && <button type="button" onClick={() => setQ("")} style={S.searchClear} aria-label="Clear"><X size={13} /></button>}
          </div>
          <div style={S.list} ref={listRef}>
            {total === 0 && <div style={S.empty}>No systems match “{q}”.</div>}
            {groups.map((g) => (
              <div key={g.host}>
                <div style={S.groupHead}>
                  <span>{g.host}</span>
                  {g.recent && <span style={S.recentTag} data-testid={`${testId}-recent`}>RECENT</span>}
                </div>
                {g.items.map((s) => {
                  const selectedOpt = s.id === value;
                  const idx = flatIndex[s.id];
                  const hot = idx === active;
                  const pending = s.data_pending || s.status === "PENDING";
                  const tag = pending ? "data pending" : (s.status && s.status !== "ACTIVE" ? String(s.status).toLowerCase() : null);
                  return (
                    <button key={s.id} type="button" data-opt-index={idx} onMouseEnter={() => !pending && setActive(idx)}
                      onClick={() => !pending && pick(s)} disabled={pending} data-testid={`${testId}-opt-${s.id}`}
                      title={pending ? "Data pending — excluded from calculations" : undefined}
                      style={{ ...S.opt, ...(hot ? S.optHot : {}), ...(selectedOpt ? S.optActive : {}), ...(pending ? { opacity: 0.45, cursor: "not-allowed" } : {}) }}>
                      <span style={S.optText}>{techniqueOf(s)}</span>
                      {tag && <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: pending ? "#8a8a8f" : "#c8821f", background: pending ? "#8a8a8f18" : "#c8821f18", borderRadius: 999, padding: "1px 7px" }}>{tag}</span>}
                      {selectedOpt && <Check size={14} style={{ flexShrink: 0, color: colors.brand }} />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

const S = {
  trigger: { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: 9, borderWidth: 1, borderStyle: "solid", borderColor: colors.border, background: "#fff", fontFamily: fonts.sans, fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left", boxSizing: "border-box" },
  triggerOpen: { borderColor: colors.brand, boxShadow: `0 0 0 3px ${colors.brand}22` },
  triggerText: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  pop: { position: "fixed", zIndex: 10000, maxWidth: "min(94vw, 440px)", background: "#fff", borderRadius: 13, border: `1px solid ${colors.border}`, boxShadow: "0 18px 60px rgba(0,0,0,0.22)", overflow: "hidden", display: "flex", flexDirection: "column" },
  searchRow: { display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: `1px solid ${colors.border}`, flexShrink: 0 },
  searchInput: { flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontFamily: fonts.sans, fontSize: 13, color: colors.textPrimary },
  searchClear: { display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.05)", color: colors.textSecondary, cursor: "pointer", flexShrink: 0 },
  list: { maxHeight: 320, overflowY: "auto", padding: "6px 0" },
  empty: { padding: "18px 14px", textAlign: "center", fontFamily: fonts.sans, fontSize: 12.5, color: colors.textTertiary },
  groupHead: { display: "flex", alignItems: "center", gap: 8, padding: "9px 14px 4px", fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: 0.8, fontWeight: 700, color: colors.textTertiary, textTransform: "uppercase" },
  recentTag: { padding: "1px 6px", borderRadius: 999, background: `${colors.brand}18`, color: colors.brand, fontSize: 8.5, letterSpacing: 0.6, fontWeight: 800 },
  opt: { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 14px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left", fontFamily: fonts.sans, fontSize: 13, color: colors.textPrimary },
  optActive: { background: `${colors.brand}12`, fontWeight: 700 },
  optHot: { background: "rgba(0,0,0,0.05)" },
  optText: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
};
